import { IClient } from '@api/models';
import { Avatar, Checkbox, CircularProgress, Input } from '@hyperflake/react-ui-library';
import { ArrowDownIcon, ChevronDownIcon, SearchIcon } from '@icon/index';
import { useState } from 'react';

interface ClientListProps {
    clients: IClient[];
    isLoading: boolean;
    selectedIds: string[];
    setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>;
    selectAllChecked: boolean;
    setSelectAllChecked: React.Dispatch<React.SetStateAction<boolean>>;
    searchValue: string;
    setSearchValue: React.Dispatch<React.SetStateAction<string>>;
}

const ClientList = ({
    clients = [],
    isLoading,
    selectedIds,
    setSelectedIds,
    setSelectAllChecked,
    searchValue,
    setSearchValue,
}: ClientListProps) => {
    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

    const filteredClients = clients
        .filter((client) => client.name.toLowerCase().includes(searchValue.toLowerCase()))
        .sort((a, b) => {
            const aSelected = selectedIds.includes(a._id);
            const bSelected = selectedIds.includes(b._id);

            if (aSelected && !bSelected) return -1;
            if (!aSelected && bSelected) return 1;

            return a.name.localeCompare(b.name);
        });

    const groupedClients = filteredClients.reduce<Record<string, IClient[]>>((acc, client) => {
        const storageName = client.storage?.name || 'No Storage';

        if (!acc[storageName]) {
            acc[storageName] = [];
        }

        acc[storageName].push(client);

        return acc;
    }, {});

    const toggleGroup = (storageName: string) => {
        setCollapsedGroups((prev) => ({
            ...prev,
            [storageName]: !prev[storageName],
        }));
    };

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        const filteredIds = filteredClients.map((client) => client._id);

        if (e.target.checked) {
            setSelectedIds((prev) => [...new Set([...prev, ...filteredIds])]);
            setSelectAllChecked(true);
        } else {
            setSelectedIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
            setSelectAllChecked(false);
        }
    };

    const handleSelectStorageGroup = (checked: boolean, groupClients: IClient[]) => {
        const groupClientIds = groupClients.map((client) => client._id);

        if (checked) {
            setSelectedIds((prev) => [...new Set([...prev, ...groupClientIds])]);
        } else {
            setSelectedIds((prev) => prev.filter((id) => !groupClientIds.includes(id)));
            setSelectAllChecked(false);
        }
    };

    const handleCheckboxChange = (checked: boolean, rowId: string) => {
        if (checked) {
            setSelectedIds((prev) => [...new Set([...prev, rowId])]);
        } else {
            setSelectedIds((prev) => prev.filter((id) => id !== rowId));
            setSelectAllChecked(false);
        }
    };

    const isAllFilteredSelected =
        filteredClients.length > 0 && filteredClients.every((client) => selectedIds.includes(client._id));

    return (
        <>
            <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-1">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2">
                        <SearchIcon className="text-grayscale-400" size={16} />
                    </div>

                    <Input
                        className="pl-9"
                        placeholder="Search for clients..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                    />
                </div>
            </div>

            <div className="flex items-center justify-between gap-3 mb-4">
                <label className="flex items-center gap-2 text-sm font-medium text-grayscale-600 cursor-pointer">
                    <Checkbox color="primary" checked={isAllFilteredSelected} onChange={handleSelectAll} />
                    Select All
                </label>

                <div className="text-xs font-medium text-grayscale-500">
                    {selectedIds.length} of {clients.length} clients selected
                </div>
            </div>

            <div className="space-y-4 max-h-[550px] overflow-y-scroll">
                {isLoading && (
                    <div className="flex items-center justify-center py-10">
                        <CircularProgress />
                    </div>
                )}

                {!isLoading &&
                    Object.entries(groupedClients).map(([storageName, groupClients]) => {
                        const groupClientIds = groupClients.map((client) => client._id);
                        const isGroupSelected =
                            groupClientIds.length > 0 && groupClientIds.every((id) => selectedIds.includes(id));

                        const isCollapsed = collapsedGroups[storageName];

                        return (
                            <div key={storageName} className="space-y-1">
                                <div className="sticky top-0 z-10 bg-white border border-grayscale-100 rounded-lg px-4 py-2 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            color="primary"
                                            checked={isGroupSelected}
                                            onChange={(e) => handleSelectStorageGroup(e.target.checked, groupClients)}
                                        />

                                        <button
                                            type="button"
                                            onClick={() => toggleGroup(storageName)}
                                            className="text-sm font-semibold text-grayscale-700 uppercase cursor-pointer"
                                        >
                                            <span className="flex items-center gap-2">
                                                <ChevronDownIcon
                                                    size={14}
                                                    className={`transition-transform ${
                                                        isCollapsed ? '-rotate-90' : 'rotate-0'
                                                    }`}
                                                />
                                                {storageName}
                                            </span>
                                        </button>
                                    </div>

                                    <span className="text-xs font-medium text-grayscale-500">
                                        {groupClients.length} clients
                                    </span>
                                </div>

                                {!isCollapsed &&
                                    groupClients.map((row) => (
                                        <label
                                            key={row._id}
                                            className={`flex items-center gap-3 cursor-pointer px-4 py-3 rounded-lg border border-grayscale-100 hover:bg-grayscale-50 transition-colors ${
                                                selectedIds.includes(row._id)
                                                    ? 'border-primary bg-primary/5'
                                                    : 'hover:bg-grayscale-100'
                                            }`}
                                        >
                                            <Avatar
                                                src={row?.logoUrl}
                                                alt={row?.name}
                                                bgColor={row?.preferences?.primaryColor}
                                            />

                                            <div className="overflow-hidden">
                                                <p className="font-medium text-sm truncate">{row.name}</p>
                                                <p className="font-medium text-xs text-grayscale-400 truncate">
                                                    {row._id}
                                                </p>
                                            </div>

                                            <div className="ml-auto">
                                                <Checkbox
                                                    color="primary"
                                                    checked={selectedIds.includes(row._id)}
                                                    onChange={(e) => handleCheckboxChange(e.target.checked, row._id)}
                                                />
                                            </div>
                                        </label>
                                    ))}
                            </div>
                        );
                    })}

                {!isLoading && filteredClients.length === 0 && (
                    <div className="font-medium text-sm text-grayscale-500">No clients found</div>
                )}
            </div>
        </>
    );
};

export default ClientList;
